// Contextual organization — the curated registry of organizational patterns
// innernet knows how to lay knowledge out in. Every project gets a default view
// chosen from its shape (what kind of knowledge it holds), a small set of
// suggested alternates, and a "+" chip where the user describes their own view
// in natural language.
//
// The registry is deliberately small and opinionated: each pattern earns its
// place by matching a real way people organize things in the world.

export type ViewKind = "essence" | "atlas" | "chronicle" | "threads" | "shelf";

export interface ViewPattern {
  kind: ViewKind;
  name: string; // chip label (lowercase, on-brand)
  /** What this pattern is, in user-readable words (shown as the view's subtitle). */
  voice: string;
  /** When innernet reaches for it. */
  whenGood: string;
}

export const VIEW_PATTERNS: Record<ViewKind, ViewPattern> = {
  essence: {
    kind: "essence",
    name: "essence",
    voice: "the heart of the project — what it is, what it knows, what changed last.",
    whenGood: "default for most projects; the orientation view.",
  },
  atlas: {
    kind: "atlas",
    name: "atlas",
    voice: "an explorable map — knowledge clustered in space around the organism.",
    whenGood: "exploratory projects and odysseys; when wandering beats scanning.",
  },
  chronicle: {
    kind: "chronicle",
    name: "chronicle",
    voice: "the story in order — every change and commit as one narrated stream.",
    whenGood: "long-running work; when 'what happened' matters most.",
  },
  threads: {
    kind: "threads",
    name: "threads",
    voice: "open loops — tasks in motion and ideas parked on branches.",
    whenGood: "active build phases with real task flow.",
  },
  shelf: {
    kind: "shelf",
    name: "shelf",
    voice: "everything in its place — the full holdings, grouped and listed plainly.",
    whenGood: "dense projects; when you know what you're looking for.",
  },
};

/** The shape of a project, as far as choosing views is concerned. */
export interface ProjectShape {
  type: string; // product | creative | research | idea | ...
  areaCount: number; // dimensions
  memoryCount: number; // nodes
  taskCount: number;
  commitCount: number;
  parkedCount: number;
}

/** Choose the default view + the suggested alternates for this project's shape.
 *  Returns 3–4 kinds, default first. */
export function suggestViews(s: ProjectShape): ViewKind[] {
  const out: ViewKind[] = [];
  const exploratory = s.type === "idea" || s.type === "creative" || s.type === "research";

  // default
  if (s.taskCount >= 4 && !exploratory) out.push("threads");
  else if (exploratory && s.memoryCount + s.areaCount >= 6) out.push("atlas");
  else out.push("essence");

  // alternates, by usefulness for this shape
  const add = (k: ViewKind) => {
    if (!out.includes(k)) out.push(k);
  };
  if (!out.includes("essence")) add("essence");
  if (s.areaCount + s.memoryCount >= 4) add("atlas");
  if (s.commitCount >= 3) add("chronicle");
  if (s.taskCount > 0 || s.parkedCount > 0) add("threads");
  add("shelf");

  return out.slice(0, 4);
}

/** Map a natural-language description to the nearest base pattern. This is the
 *  draft-tier resolver — keyword heuristics standing in for the reorganizer
 *  agent, so a described view lands somewhere sensible immediately. */
export function resolveDescribedView(desc: string): { kind: ViewKind; title: string } {
  const d = desc.toLowerCase();
  const title = desc.trim().slice(0, 42);
  if (/(time|when|history|order|story|log|journal|chrono)/.test(d)) return { kind: "chronicle", title };
  if (/(task|todo|doing|progress|loop|thread|kanban|status)/.test(d)) return { kind: "threads", title };
  if (/(map|explore|spatial|cluster|graph|visual|atlas|space)/.test(d)) return { kind: "atlas", title };
  if (/(file|folder|list|index|alphabet|group|shelf|library|all)/.test(d)) return { kind: "shelf", title };
  return { kind: "essence", title };
}

/** Knowledge depth — the abstract, user-facing measure of how much a project
 *  holds. Five steps, named like growth, never counted like a database. */
export function knowledgeDepth(areaCount: number, memoryCount: number): {
  step: 1 | 2 | 3 | 4 | 5;
  word: string;
} {
  const mass = areaCount * 2 + memoryCount;
  const step = mass <= 2 ? 1 : mass <= 6 ? 2 : mass <= 14 ? 3 : mass <= 30 ? 4 : 5;
  const word = ["sprouting", "rooting", "branching", "deep", "vast"][step - 1];
  return { step: step as 1 | 2 | 3 | 4 | 5, word };
}

/** A descriptive line every project carries when the user hasn't written one —
 *  no project exists without a summary. */
export function fallbackSummary(_name: string, type: string, areaCount: number, memoryCount: number): string {
  const depth = knowledgeDepth(areaCount, memoryCount).word;
  const what =
    type === "creative" ? "a creative direction" :
    type === "research" ? "a line of inquiry" :
    type === "idea" ? "an idea taking shape" :
    type === "infrastructure" ? "a system being built" :
    "a project in motion";
  return `${what} — its memory is ${depth}, and growing as you work.`;
}
